import { logger } from '@/utils/logger';

/**
 * Audio Element Pool Service
 * Pre-creates and caches HTMLAudioElement instances for instant playback
 * When user clicks play, audio is already loaded and ready
 */
class AudioElementPoolService {
  private pool = new Map<string, HTMLAudioElement>();
  private readyState = new Map<string, boolean>();
  private currentCourseId: string | null = null;
  private readonly MAX_POOL_SIZE = 5;

  /**
   * Pre-create audio element with URL already loaded
   * Audio will start downloading immediately in background
   */
  preload(moduleId: string, audioUrl: string, courseId?: string): void {
    if (!audioUrl || !moduleId) return;
    
    // Clear pool if course changed
    if (courseId && this.currentCourseId && this.currentCourseId !== courseId) {
      logger.info(`[AudioPool] Course changed, clearing pool`);
      this.clearAll();
    }
    if (courseId) {
      this.currentCourseId = courseId;
    }

    // Already in pool
    if (this.pool.has(moduleId)) {
      logger.debug(`[AudioPool] Already in pool: ${moduleId}`);
      return;
    }

    // Enforce max pool size (evict oldest)
    if (this.pool.size >= this.MAX_POOL_SIZE) {
      const oldestKey = this.pool.keys().next().value;
      if (oldestKey) {
        this.evict(oldestKey);
      }
    }

    try {
      const audio = new Audio();
      audio.crossOrigin = 'anonymous';
      audio.preload = 'auto'; // Download full audio data
      
      // Track ready state
      audio.addEventListener('canplaythrough', () => {
        this.readyState.set(moduleId, true);
        logger.info(`⚡ [AudioPool] READY for instant play: ${moduleId}`);
      });

      audio.addEventListener('loadedmetadata', () => {
        logger.debug(`[AudioPool] Metadata loaded: ${moduleId} (${Math.round(audio.duration)}s)`);
      });

      audio.addEventListener('error', (e) => {
        logger.warn(`[AudioPool] Failed to preload: ${moduleId}`, e);
        this.readyState.set(moduleId, false);
        this.pool.delete(moduleId);
      });

      // Set src and start loading
      audio.src = audioUrl;
      audio.load();

      this.pool.set(moduleId, audio);
      logger.info(`[AudioPool] 📥 Preloading: ${moduleId}`);
    } catch (error) {
      logger.warn(`[AudioPool] Failed to create audio element:`, error);
    }
  }

  /**
   * Get pre-created audio element for instant playback
   * Returns null if not in pool
   */
  get(moduleId: string): HTMLAudioElement | null {
    const audio = this.pool.get(moduleId);
    if (audio) {
      // Remove from pool since it will be used
      this.pool.delete(moduleId);
      logger.info(`⚡ [AudioPool] Retrieved pre-loaded element: ${moduleId}`);
      return audio;
    }
    return null;
  }

  /**
   * Check if audio element exists and is ready to play
   * readyState >= 3 means HAVE_FUTURE_DATA (can play)
   */
  isReady(moduleId: string): boolean {
    const audio = this.pool.get(moduleId);
    if (!audio) return false;
    
    // Check both our tracked state and the actual readyState
    const tracked = this.readyState.get(moduleId) === true;
    const actual = audio.readyState >= 3; // HAVE_FUTURE_DATA or HAVE_ENOUGH_DATA
    
    return tracked || actual;
  }

  /**
   * Prefetch next module in course sequence
   * Called when user starts playing current module
   */
  async prefetchNext(courseId: string, currentModuleId: string, allModuleIds: string[]): Promise<void> {
    const currentIndex = allModuleIds.indexOf(currentModuleId);
    if (currentIndex === -1 || currentIndex >= allModuleIds.length - 1) return;

    const nextModuleId = allModuleIds[currentIndex + 1];
    
    // Skip if already in pool or ready
    if (this.pool.has(nextModuleId) || this.isReady(nextModuleId)) {
      logger.debug(`[AudioPool] Next module already loaded: ${nextModuleId}`);
      return;
    }

    // Get audio URL from metadata cache
    try {
      const { audioMetadataCache } = await import('./AudioMetadataCache');
      const cached = audioMetadataCache.get(nextModuleId, courseId);
      
      if (cached?.audioUrl) {
        this.preload(nextModuleId, cached.audioUrl, courseId);
        logger.info(`[AudioPool] 🔮 Pre-loading next module: ${nextModuleId}`);
      }
    } catch (error) {
      logger.warn(`[AudioPool] Failed to prefetch next:`, error);
    }
  }

  /**
   * Evict a single module from pool
   */
  private evict(moduleId: string): void {
    const audio = this.pool.get(moduleId);
    if (audio) {
      audio.pause();
      audio.src = '';
      this.pool.delete(moduleId);
      this.readyState.delete(moduleId);
      logger.debug(`[AudioPool] Evicted: ${moduleId}`);
    }
  }

  /**
   * Clear all audio elements from pool
   */
  clearAll(): void {
    this.pool.forEach((audio, moduleId) => {
      audio.pause();
      audio.src = '';
    });
    this.pool.clear();
    this.readyState.clear();
    this.currentCourseId = null;
    logger.info(`[AudioPool] Cleared all`);
  }

  /**
   * Get pool statistics for debugging
   */
  getStats(): { size: number; ready: number; moduleIds: string[] } {
    const readyCount = Array.from(this.readyState.values()).filter(v => v).length;
    return {
      size: this.pool.size,
      ready: readyCount,
      moduleIds: Array.from(this.pool.keys()),
    };
  }
}

export const audioElementPool = new AudioElementPoolService();
